import { useUploadOptional } from '@/shared/contexts';
import { Button, Card, Loader, Progress } from '@/shared/components/ui';
import {
    Upload,
    X,
    ChevronDown,
    ChevronUp,
    CheckCircle,
    AlertCircle,
    MinusCircle,
    Layers,
    ExternalLink,
    XCircle,
    Clock,
} from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { TableUploadService } from '@/features/table-upload';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export function UploadIndicator() {
    const { t } = useTranslation('shared');
    const uploadContext = useUploadOptional();
    const location = useLocation();
    const navigate = useNavigate();

    if (!uploadContext) return null;

    const {
        uploads,
        activeBatches,
        isMinimized,
        setIsMinimized,
        clearUpload,
        clearAllCompleted,
        dismissBatch,
    } = uploadContext;

    const isOnUploadTab =
        location.pathname.includes('/project/') &&
        location.search.includes('tab=upload');

    const visibleBatches = isOnUploadTab ? [] : activeBatches;
    const hasAnything = uploads.length > 0 || visibleBatches.length > 0;

    if (!hasAnything) return null;

    const activeUploads = uploads.filter(
        (u) =>
            u.status === 'uploading' ||
            u.status === 'processing' ||
            u.status === 'annotating' ||
            u.status === 'pending',
    );
    const completedUploads = uploads.filter(
        (u) => u.status === 'success' || u.status === 'error',
    );
    const activeBatchCount = visibleBatches.filter((b) => !b.allDone).length;
    const hasActive = activeUploads.length > 0 || activeBatchCount > 0;
    const totalActive = activeUploads.length + activeBatchCount;

    /* ── helpers ── */
    const statusIcon = (status: string) => {
        switch (status) {
            case 'success':    return <CheckCircle className="h-3.5 w-3.5 text-green-500" />;
            case 'error':      return <XCircle className="h-3.5 w-3.5 text-red-500" />;
            case 'uploading':
            case 'processing':
            case 'annotating': return <Loader size="sm" />;
            default:           return <Clock className="h-3.5 w-3.5 text-muted-foreground/60" />;
        }
    };

    const statusText = (status: string) => {
        switch (status) {
            case 'pending':    return t('upload.waiting');
            case 'uploading':  return t('upload.sending');
            case 'processing': return t('upload.processing');
            case 'annotating': return t('upload.annotating');
            case 'success':    return t('upload.completed');
            case 'error':      return t('upload.error');
            default:           return status;
        }
    };

    const statusColor = (status: string) =>
        status === 'success'
            ? 'text-green-600 dark:text-green-400'
            : status === 'error'
            ? 'text-red-500'
            : status === 'processing' || status === 'uploading' || status === 'annotating'
            ? 'text-visagio-teal'
            : 'text-muted-foreground';

    const batchItemIcon = (status: string) => {
        switch (status) {
            case 'success':    return <CheckCircle className="h-3 w-3 text-green-500" />;
            case 'error':      return <XCircle className="h-3 w-3 text-red-500" />;
            case 'processing': return <Loader size="sm" />;
            default:           return <Clock className="h-3 w-3 text-muted-foreground/50" />;
        }
    };

    return (
        <Card className={cn(
            'fixed bottom-4 right-4 z-50 shadow-xl border overflow-hidden transition-all duration-300',
            isMinimized ? 'w-auto' : 'w-[22rem]',
        )}>
            {/* ── Header ── */}
            <div
                className={cn(
                    'flex items-center justify-between px-4 py-3 cursor-pointer',
                    'border-b bg-card hover:bg-muted/40 transition-colors',
                )}
                onClick={() => setIsMinimized(!isMinimized)}
            >
                <div className="flex items-center gap-2.5">
                    {/* Status dot */}
                    <div className={cn(
                        'w-2 h-2 rounded-full shrink-0',
                        hasActive ? 'bg-visagio-teal animate-pulse' : 'bg-green-500',
                    )} />
                    <span className="text-sm font-semibold leading-none">
                        {t('upload.uploads')}
                    </span>
                    {totalActive > 0 && (
                        <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-visagio-teal/10 text-visagio-teal">
                            {totalActive}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {completedUploads.length > 0 && !isMinimized && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                            title="Limpar concluídos"
                            onClick={(e) => { e.stopPropagation(); clearAllCompleted(); }}
                        >
                            <MinusCircle className="h-3.5 w-3.5" />
                        </Button>
                    )}
                    {isMinimized
                        ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    }
                </div>
            </div>

            {/* ── Content ── */}
            {!isMinimized && (
                <div className="max-h-80 overflow-y-auto divide-y">

                    {/* ── Uploads individuais ── */}
                    {uploads.map((upload) => (
                        <div key={upload.id} className="px-4 py-3">
                            <div className="flex items-start gap-2.5">
                                <div className="mt-0.5 shrink-0">{statusIcon(upload.status)}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-1">
                                        <p className="text-xs font-medium truncate leading-snug" title={upload.fileName}>
                                            {upload.fileName}
                                        </p>
                                        {(upload.status === 'success' || upload.status === 'error') && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0 shrink-0 -mt-0.5 text-muted-foreground hover:text-foreground"
                                                onClick={() => clearUpload(upload.id)}
                                            >
                                                <X className="h-3 w-3" />
                                            </Button>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <span className="text-xs text-muted-foreground">
                                            {TableUploadService.formatBytes(upload.fileSize)}
                                        </span>
                                        <span className="text-muted-foreground/40 text-xs">·</span>
                                        <span className={cn('text-xs font-medium', statusColor(upload.status))}>
                                            {statusText(upload.status)}
                                        </span>
                                    </div>

                                    {(upload.status === 'uploading' ||
                                        upload.status === 'processing' ||
                                        upload.status === 'annotating') && (
                                        <div className="mt-2">
                                            <Progress value={upload.progress} className="h-1" />
                                            {upload.message && (
                                                <p className="text-xs text-muted-foreground mt-1 truncate">
                                                    {upload.message}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {upload.status === 'error' && upload.error && (
                                        <p className="text-xs text-red-500 mt-1 truncate" title={upload.error}>
                                            {upload.error}
                                        </p>
                                    )}

                                    {upload.status === 'success' && upload.result?.table_name && (
                                        <p className="text-xs text-green-600 dark:text-green-400 mt-1 font-mono truncate">
                                            {upload.result.table_name}
                                            {upload.result.row_count
                                                ? ` · ${t('upload.rowCount', { count: upload.result.row_count })}`
                                                : ''}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* ── Batch uploads ── */}
                    {visibleBatches.map((batch) => {
                        const doneCount = batch.items.filter(
                            (i) => i.status === 'success' || i.status === 'error',
                        ).length;
                        const errorCount = batch.items.filter((i) => i.status === 'error').length;
                        const successCount = doneCount - errorCount;

                        return (
                            <div key={batch.id} className="px-4 py-3">
                                {/* Batch header row */}
                                <div className="flex items-start gap-2.5 mb-2.5">
                                    <div className="mt-0.5 shrink-0">
                                        {batch.allDone
                                            ? errorCount === 0
                                                ? <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                                                : <AlertCircle className="h-3.5 w-3.5 text-yellow-500" />
                                            : <Loader size="sm" />
                                        }
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-1">
                                            <div className="flex items-center gap-1 min-w-0">
                                                <Layers className="h-3 w-3 text-visagio-teal shrink-0" />
                                                <p className="text-xs font-medium truncate leading-snug" title={batch.fileName}>
                                                    {batch.fileName}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-0.5 shrink-0">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                                                    title="Ver uploads do projeto"
                                                    onClick={() => navigate(`/dashboard/project/${batch.projectId}?tab=upload`)}
                                                >
                                                    <ExternalLink className="h-3 w-3" />
                                                </Button>
                                                {batch.allDone && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground"
                                                        onClick={() => dismissBatch(batch.id)}
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {batch.allDone
                                                ? errorCount === 0
                                                    ? `${successCount} tabela${successCount !== 1 ? 's' : ''} criada${successCount !== 1 ? 's' : ''}`
                                                    : `${successCount} criada${successCount !== 1 ? 's' : ''} · ${errorCount} com erro`
                                                : `${doneCount} de ${batch.items.length} concluída${doneCount !== 1 ? 's' : ''}`
                                            }
                                        </p>
                                    </div>
                                </div>

                                {/* Per-sheet rows */}
                                <div className="space-y-2 pl-6 border-l-2 border-muted ml-1.5">
                                    {batch.items.map((item) => (
                                        <div key={item.upload_id}>
                                            <div className="flex items-center gap-1.5">
                                                {batchItemIcon(item.status)}
                                                <span className="text-xs truncate flex-1" title={item.sheet_name}>
                                                    {item.sheet_name}
                                                </span>
                                                <span className={cn(
                                                    'text-xs shrink-0 font-medium',
                                                    item.status === 'success' && 'text-green-600 dark:text-green-400',
                                                    item.status === 'error' && 'text-red-500',
                                                    (item.status === 'pending' || item.status === 'processing') && 'text-muted-foreground',
                                                )}>
                                                    {item.status === 'success'
                                                        ? '✓'
                                                        : item.status === 'error'
                                                        ? '✗'
                                                        : item.status === 'processing'
                                                        ? `${Math.round(item.percentage)}%`
                                                        : '—'}
                                                </span>
                                            </div>
                                            {(item.status === 'pending' || item.status === 'processing') && (
                                                <Progress value={item.percentage} className="h-0.5 mt-1 ml-4" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
}

export default UploadIndicator;
